import type { ChildGuideItem } from '@tunein/web-api-client';
import { isTopic, lotame } from '@tunein/web-common';
import {
  PlayerManager,
  type LoadOptions as PlayerManagerLoadOptions,
  type PlayerManagerOptions,
} from '@tunein/web-player-manager';
import { registerPlayerManagerEvents } from '@tunein/web-player-manager/react';
import { Singleton, type SingletonInstance } from '@tunein/web-utils';
import { isNotNil } from 'es-toolkit';
import type { History } from 'history';
import isEmpty from 'lodash/isEmpty';
import { fbPixelCustomEvents, fbTrack } from 'src/client/analytics/facebook/fbTrack';
import { apiBeta } from 'src/client/api';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { stores } from 'src/client/stores';
import { getSearchParam } from 'src/client/utils/getSearchParam.ts';
import {
  determineInAppMessageDialogRequestBehavior,
  openDownloadAppDialogAndTrack,
  openNowPlayingDialog,
  openUpsellAndTrackActivity,
} from 'src/common/actions/dialog';
import { logClientError, logFeatureActivity } from 'src/common/actions/logging';
import canShowInstallAppDialog from 'src/common/actions/utils/dialog/canShowInstallAppDialog';
import createAndOpenSubscribeLink from 'src/common/components/utils/createAndOpenSubscribeLink';
import feature from 'src/common/constants/analytics/categoryActionLabel/feature';
import { labels } from 'src/common/constants/analytics/categoryActionLabel/subscribe';
import { BRAZE_PLAY_EVENT } from 'src/common/constants/braze';
import { PLAYER_MANAGER_ENABLED } from 'src/common/constants/experiments/config';
import { BYPASS_UPSELL_DIALOG_ENABLED } from 'src/common/constants/experiments/dialog';
import { PLAY_OPEN_IN_APP_ENABLED } from 'src/common/constants/experiments/redirect';
import {
  selectBranchUrl,
  selectFirstVisitStartTime,
  selectIsBrazeInitialized,
  selectIsMobile,
} from 'src/common/selectors/app';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { selectBreadcrumbId } from 'src/common/selectors/reporting';
import { brazeLogCustomEvent } from 'src/common/utils/braze/brazeHelper';
import { isInDiscordIFrame } from 'src/common/utils/discord';
import { PLATFORM_ENV_SHORTHAND } from 'src/common/utils/environment';
import getGuideItemPathname from 'src/common/utils/guideItem/getGuideItemPathname';
import { behaviors as GuideItemBehaviors, isProfile } from 'src/common/utils/guideItemTypes';
import { mintSingleton } from '../mint';
import { popoutPlayer } from '../popout';
import { buildConsentData } from './utils';

interface LoadOptions extends PlayerManagerLoadOptions {
  // This is temporary until we completely migrate PM and WAC. This field will be derived in WAC by the help of the WAC
  // init option `uiServiceOptions.generateRoutePathForGuideItem`
  guideItemPathname?: string;
}

type PlayerOptions = Required<PlayerManagerOptions>['playerOptions'];

export type PlayerSingletonOptions = {
  history: History;
  playerOptions?: Pick<PlayerOptions, 'volume'>;
} & Pick<PlayerManagerOptions, 'localizations' | 'enableFetchAutoplayGuideItem'>;

export class PlayerSingleton extends Singleton<PlayerManager> {
  #didUseSearchParam = false;
  #canAutoplay = true;
  #history: History | null = null;
  #autoplayGuideItem: ChildGuideItem | null = null;

  async init(options: PlayerSingletonOptions) {
    const { localizations, enableFetchAutoplayGuideItem, history, playerOptions } = options;
    const state = storeSingleton.state;
    this.#history = history;

    const playerManager = new PlayerManager({
      api: apiBeta.instance,
      playerOptions: {
        volume: playerOptions?.volume,
      },
      localizations,
      enableFetchAutoplayGuideItem,
      appSessionData: {
        consentDataPromise: buildConsentData(),
        isInDiscordIframe: isInDiscordIFrame(),
        breadcrumbId: selectBreadcrumbId(state),
        env: PLATFORM_ENV_SHORTHAND,
        firstVisitTimestampMs: selectFirstVisitStartTime(state),
      },
    });

    // IMPORTANT: must be called prior to init below so that we can capture the autoplay metadata, if available
    registerPlayerManagerEvents(playerManager, stores.playerStore);

    await playerManager.init();
    popoutPlayer.init(playerManager);

    this.instance = playerManager;

    this.#setupListeners();
  }

  /**
   * TODO: this can be deleted after the experiment. This is a handy shorthand to help with deciding whether the
   *  experiment is enabled or not.
   */
  get isActive() {
    return apiBeta.getConfigSetting(PLAYER_MANAGER_ENABLED);
  }

  /**
   * Toggle to ensure that an autoplay action won't override a user-initiated play request
   */
  get canAutoplay() {
    return this.#canAutoplay;
  }

  onClick = async (guideItem?: ChildGuideItem, options?: LoadOptions) => {
    this.#canAutoplay = false;

    if (!this.isReady) {
      return;
    }

    if (!options?.isUserInitiated && getSearchParam('disableAutoplay')) {
      return;
    }

    const guideItemToPlay = this.#getGuideItemToPlay(guideItem);

    if (guideItemToPlay) {
      const canContinue = this.#handleShortCircuits(guideItemToPlay);

      if (canContinue) {
        this.#loadAndPlayGuideItem(guideItemToPlay as ChildGuideItem, options);
      }

      return;
    }

    // Prevents programmatic requests (e.g., async autoplay) from interrupting user-initiated playback
    if (guideItem && options?.isUserInitiated === false) {
      return;
    }

    if (this.instance.isPlaying()) {
      this.instance.isLive() && !this.instance.isLiveSeek()
        ? this.instance.stop()
        : this.instance.pause();
      return;
    }

    if (this.instance.isPaused()) {
      this.instance.play();
    }
  };

  // Convenience wrapper to allow passing reference without consuming args
  playNext = () => {
    return this.instance.playNext();
  };

  seekTo = (seekTime: number) => {
    stores.playerStore.getState().setCurrentTime(seekTime);
    this.instance.seekTo(seekTime);
  };

  seekBackward(seekOffset: number) {
    this.instance.seekBackward(seekOffset);
  }

  seekForward(seekOffset: number) {
    this.instance.seekForward(seekOffset);
  }

  async setAppSessionData(appSessionData: Parameters<PlayerManager['setAppSessionData']>[0]) {
    await this.ready;

    this.instance.setAppSessionData(appSessionData);
  }

  /**
   * Overrides the start position of the topic and is only valid for the initial load of the app session
   */
  get #positionFromSearchParam() {
    const position = this.#didUseSearchParam ? 0 : +getSearchParam('st');

    this.#didUseSearchParam = true;

    return position;
  }

  #setupListeners() {
    const { setCurrentGuideItem } = stores.playerStore.getState();
    const { setGuideItemPathname } = stores.appStore.getState();

    this.instance.on('metadata', (metadata) => {
      lotame.addInterests(metadata);

      // TODO: investiaget where this "popup" data originates (NP?). Might need to do some archeology on older Gemini Web commits.
      // We use it for stations with live seek (i.e. FOX News Radio) - display dialog about rewind and live listening.
      // @ts-expect-error
      const inAppMessageKey = metadata?.popup?.destinationInfo?.id;

      if (inAppMessageKey && !storeSingleton.state.dialog.shouldInAppMessageDialogOpen) {
        storeSingleton.dispatch(
          determineInAppMessageDialogRequestBehavior(inAppMessageKey, metadata.primaryGuideId),
        );
      }
    });

    this.instance.on('nextguideitem', async (nextGuideItem) => {
      setGuideItemPathname(getGuideItemPathname(nextGuideItem));
    });

    // Handle ads companions
    this.instance.on('companionAds', ({ companionAds, requestId, provider }) => {
      mintSingleton.instance?.onCompanionAdsLoaded({
        companions: companionAds,
        requestId,
        adProvider: provider,
      });
    });

    const resetDisplayAdSlots = () => {
      mintSingleton.instance?.resetDisplayAdSlots();
    };

    this.instance.on('adcomplete', resetDisplayAdSlots);
    this.instance.on('adskipped', resetDisplayAdSlots);
    this.instance.on('adplaybackcancelled', resetDisplayAdSlots);

    // Non-topics that end with a profile next guide id should redirect to the profile page.
    this.instance.on('ended', async () => {
      const { currentGuideItem } = stores.playerStore.getState();

      if (
        !isTopic(currentGuideItem?.playableGuideId) &&
        isProfile(this.instance.currentStream?.nextGuideId)
      ) {
        this.#history?.replace(`/radio/${this.instance.currentStream.nextGuideId}/`);
      }
    });

    this.instance.on('topicguideitem', (guideItem) => setCurrentGuideItem(guideItem));

    // TODO: add this state to the React store from Player Manager
    this.instance.on('autoplayguideitem', (guideItem: ChildGuideItem) => {
      this.#autoplayGuideItem = guideItem;
    });
  }

  #getCachedPosition(guideItem: ChildGuideItem) {
    const positionFromSearchParam = this.#positionFromSearchParam;

    if (positionFromSearchParam) {
      return positionFromSearchParam;
    }

    const { time, percent } =
      stores.playerStore.getState().playbackProgressHistory[guideItem.playableGuideId as string] ||
      {};

    return percent === 100 ? 0 : time;
  }

  #sendPreLoadMetrics(guideItem: ChildGuideItem, options?: LoadOptions) {
    fbTrack(fbPixelCustomEvents.StartPlay, {
      guideId: guideItem.playableGuideId,
      isAutoplayed: isNotNil(options?.isUserInitiated) ? !options.isUserInitiated : false,
    });

    const isAutoPlayingRecents = guideItem === this.#autoplayGuideItem;

    if (isAutoPlayingRecents) {
      const { actions, labels: featureLabel } = feature;
      storeSingleton.dispatch(logFeatureActivity(actions.autoPlay, featureLabel.playMiniPlayer));
    }
  }

  #sendPostLoadMetrics(guideItem: ChildGuideItem) {
    const state = storeSingleton.state;
    const isBrazeInitialized = selectIsBrazeInitialized(state);

    if (isBrazeInitialized) {
      try {
        const { metadata } = stores.playerStore.getState();

        if (!metadata) {
          return;
        }

        // By sending this custom event to Braze triggers to show the In-App Message. Note: Make sure it happens after the preroll has finished playing.
        brazeLogCustomEvent(BRAZE_PLAY_EVENT, {
          guideId: guideItem.isTopic ? guideItem.parentGuideId : guideItem.playableGuideId,
          boostEnabledStation: !isEmpty(metadata.boost),
          title: metadata?.header?.title || metadata?.title,
          ...metadata?.classification,
          ...(guideItem.isTopic && {
            topicId: guideItem.playableGuideId,
          }),
        });
      } catch (err) {
        storeSingleton.dispatch(
          logClientError({
            message: `Braze Custom Event: ${BRAZE_PLAY_EVENT} Error`,
            context: {
              error: err,
            },
          }),
        );
        // NOTE: Don't break app functionality for Braze
      }
    }
  }

  #handleShortCircuits(guideItem: ChildGuideItem) {
    // regular upsell triggered by attempt to play a locked item
    // this instance should only be triggered on the large profile play button
    // because the lock tile covers other cases
    // - blocks playback
    const behaviorAction = guideItem.behaviors?.primaryButton?.actionName;

    if (behaviorAction === GuideItemBehaviors.subscribe) {
      this.#handleSubscribeAction(guideItem);
      return;
    }

    if (!guideItem.isPlayable) {
      return;
    }

    if (this.#handleBranchRedirect()) {
      return;
    }

    return true;
  }

  #handleBranchRedirect() {
    const state = storeSingleton.state;

    // on attempt to play, open via branch deep link
    const isMobile = selectIsMobile(state);
    const branchUrl = selectBranchUrl(state);
    const playOpenInAppStore = apiBeta.getConfigSetting(PLAY_OPEN_IN_APP_ENABLED);

    if (isMobile && playOpenInAppStore && branchUrl) {
      window.location.replace(branchUrl);

      return true;
    }
  }

  #handleSubscribeAction(guideItem: ChildGuideItem) {
    if (apiBeta.getConfigSetting(BYPASS_UPSELL_DIALOG_ENABLED, false)) {
      createAndOpenSubscribeLink(guideItem, location, null, 'tune.action');
      return;
    }

    return storeSingleton.dispatch(
      openUpsellAndTrackActivity(labels.profileUpsellDialog, guideItem.playableGuideId),
    );
  }

  #getGuideItemToPlay(guideItem?: ChildGuideItem) {
    // TODO: this can be deleted after WAC is completely migrated
    if (guideItem) {
      guideItem.playableGuideId = guideItem.actions?.play?.guideId || guideItem.guideId;
    }

    const { error, currentGuideItem, metadata, popout } = stores.playerStore.getState();
    const isNewGuideItem =
      guideItem && guideItem.playableGuideId !== currentGuideItem?.playableGuideId;
    const isCurrentGuideItemIdle =
      currentGuideItem && this.instance.isIdle() && !metadata?.rejectReason && !popout.showButton;
    const shouldLoadAndPlayGuideItem = isNewGuideItem || isCurrentGuideItemIdle || Boolean(error);
    const guideItemToPlay = guideItem || currentGuideItem;

    if (shouldLoadAndPlayGuideItem) {
      return guideItemToPlay as ChildGuideItem;
    }
  }

  async #loadAndPlayGuideItem(guideItem: ChildGuideItem, options?: LoadOptions) {
    const { setCurrentGuideItem, setPopoutState } = stores.playerStore.getState();

    setCurrentGuideItem(guideItem);
    setPopoutState({ showButton: false });

    if (!options?.guideItemPathname) {
      const { setGuideItemPathname } = stores.appStore.getState();

      setGuideItemPathname(getGuideItemPathname(guideItem, undefined));
    }

    this.#sendPreLoadMetrics(guideItem, options);
    this.#updateAppSessionData();
    await this.instance.load(guideItem, {
      ...options,
      position: this.#getCachedPosition(guideItem),
    });

    if (!this.instance.isLoaded()) {
      return;
    }

    this.#playSideEffects(guideItem);
    await this.instance.play();
  }

  #playSideEffects(guideItem: ChildGuideItem) {
    const state = storeSingleton.state;
    const isNowPlayingDialogOpen = selectIsNowPlayingDialogOpen(state);

    if (!isNowPlayingDialogOpen && canShowInstallAppDialog(state)) {
      storeSingleton.dispatch(openDownloadAppDialogAndTrack());
    } else {
      storeSingleton.dispatch(openNowPlayingDialog());
    }

    this.#sendPostLoadMetrics(guideItem);
  }

  async #updateAppSessionData() {
    const state = storeSingleton.state;

    this.instance.setAppSessionData({
      consentDataPromise: buildConsentData(),
      breadcrumbId: selectBreadcrumbId(state),
    });
  }
}

export const player = new PlayerSingleton() as SingletonInstance<PlayerManager, PlayerSingleton>;
